import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  AboveGradeWallType,
  BelowGradeWallType,
  ExposureDirectionType,
  FootprintPolygonsType,
  SizingCalculatorAttributesType,
  SizingItemsType,
  SpaceType
} from 'data/types';
import { setDefaultSizingItems } from 'features/data/data-slice';
import { reconcileSpaceWalls } from 'features/structure/structure-thunks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createCeilingCrudFunctions,
  createFloorCrudFunctions,
  createWallCrudFunctions,
  groupWallsByEdge,
  isAboveGradeWall
} from 'utils/sizing-crud';
import { findWallByFenestrationId } from 'utils/structure';
import CeilingBelowAtticComponent from '../sizing/ceilings/ceiling-below-attic';
import CeilingBelowRoofComponent from '../sizing/ceilings/ceiling-below-roof';
import BasementFloorComponent from '../sizing/floors/basement-floor';
import FloorOverExposedSpaceComponent from '../sizing/floors/floor-over-exposed-space';
import FloorOverUnconditionedSpaceComponent from '../sizing/floors/floor-over-unconditioned-space';
import SlabFloorComponent from '../sizing/floors/slab-floor';
import AboveGradeWallComponent from '../sizing/walls/above-grade-wall';
import BelowGradeWallComponent from '../sizing/walls/below-grade-wall';
import SpacePolygonWalls from './space-polygon-walls';

type WallsAroundSpaceProps = {
  space: Pick<
    SpaceType,
    'uniqueId' | 'vertices' | 'width' | 'length' | 'position'
  > | null;
  footprints: FootprintPolygonsType | null;
  sizingAttributes: SizingCalculatorAttributesType | undefined;
  updateSizingAttributes: (
    updates: Partial<SizingCalculatorAttributesType>
  ) => void;
  homeOrientation: ExposureDirectionType | undefined;
  defaultArea: number;
  defaultWallWidth: number;
  defaultWallHeight: number;
  focusFenestrationId?: string;
};

export default function WallsAroundSpace({
  space,
  footprints,
  sizingAttributes,
  updateSizingAttributes,
  homeOrientation,
  defaultArea,
  defaultWallWidth,
  defaultWallHeight,
  focusFenestrationId
}: WallsAroundSpaceProps) {
  const dispatch = useAppDispatch();
  const { defaultSizingItems } = useAppSelector((state) => state.data);
  const [selectedWallId, setSelectedWallId] = useState<string | null>(null);
  const autoSelectedFocusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!space?.uniqueId) return;
    dispatch(
      reconcileSpaceWalls({
        requests: [{ spaceId: space.uniqueId, previousSpace: null }]
      })
    );
  }, [dispatch, space?.uniqueId]);

  useEffect(() => {
    if (!focusFenestrationId) {
      autoSelectedFocusRef.current = undefined;
      return;
    }
    if (autoSelectedFocusRef.current === focusFenestrationId) return;

    const wall = findWallByFenestrationId(
      sizingAttributes,
      focusFenestrationId
    );
    if (wall) {
      setSelectedWallId(wall.uniqueId);
      autoSelectedFocusRef.current = focusFenestrationId;
    }
  }, [focusFenestrationId, sizingAttributes]);

  const wallsByEdge = useMemo(() => {
    const aboveGradeWalls = sizingAttributes?.aboveGradeWalls || [];
    const belowGradeWalls = sizingAttributes?.belowGradeWalls || [];
    return groupWallsByEdge(aboveGradeWalls, belowGradeWalls, homeOrientation);
  }, [sizingAttributes, homeOrientation]);

  const wallFns = useMemo(
    () =>
      createWallCrudFunctions(
        sizingAttributes,
        updateSizingAttributes,
        defaultSizingItems,
        defaultWallWidth,
        defaultWallHeight,
        homeOrientation
      ),
    [
      sizingAttributes,
      updateSizingAttributes,
      defaultSizingItems,
      defaultWallWidth,
      defaultWallHeight,
      homeOrientation
    ]
  );

  const defaultFloorExposedEdge = useMemo(() => {
    const aboveGradeWalls = sizingAttributes?.aboveGradeWalls || [];
    return aboveGradeWalls.reduce((acc, wall) => acc + wall.width, 0);
  }, [sizingAttributes]);

  const ceilingFns = useMemo(
    () =>
      createCeilingCrudFunctions(
        sizingAttributes,
        updateSizingAttributes,
        defaultSizingItems,
        defaultArea
      ),
    [sizingAttributes, updateSizingAttributes, defaultSizingItems, defaultArea]
  );

  const floorFns = useMemo(
    () =>
      createFloorCrudFunctions(
        sizingAttributes,
        updateSizingAttributes,
        defaultSizingItems,
        defaultArea,
        defaultFloorExposedEdge,
        defaultWallWidth
      ),
    [
      sizingAttributes,
      updateSizingAttributes,
      defaultSizingItems,
      defaultArea,
      defaultFloorExposedEdge,
      defaultWallWidth
    ]
  );

  const applyToDefaultSizingItems = (updates: Partial<SizingItemsType>) => {
    dispatch(
      setDefaultSizingItems({
        ...defaultSizingItems,
        ...updates
      })
    );
  };

  const getSwitchDisabledReason = (
    wall: AboveGradeWallType | BelowGradeWallType
  ): string | undefined => {
    if (isAboveGradeWall(wall)) {
      const collides = (sizingAttributes?.belowGradeWalls ?? []).some(
        (w) => w.exposure === wall.exposure
      );
      return collides ? 'This side already has a below grade wall' : undefined;
    }
    const collides = (sizingAttributes?.aboveGradeWalls ?? []).some(
      (w) => w.exposure === wall.exposure
    );
    return collides ? 'This side already has an above grade wall' : undefined;
  };

  const renderWall = (
    wall: AboveGradeWallType | BelowGradeWallType,
    opts?: { defaultExpandAdvanced?: boolean }
  ) =>
    isAboveGradeWall(wall) ? (
      <AboveGradeWallComponent
        key={wall.uniqueId}
        wall={wall}
        updateFn={wallFns.updateAboveGradeWall}
        duplicateFn={wallFns.duplicateAboveGradeWall}
        deleteFn={wallFns.deleteWall}
        switchGradeFn={wallFns.switchWallGrade}
        switchDisabledReason={getSwitchDisabledReason(wall)}
        updateDefaultSizingFn={applyToDefaultSizingItems}
        defaultExpandAdvanced={opts?.defaultExpandAdvanced}
        focusFenestrationId={focusFenestrationId}
      />
    ) : (
      <BelowGradeWallComponent
        key={wall.uniqueId}
        wall={wall}
        updateFn={wallFns.updateBelowGradeWall}
        duplicateFn={wallFns.duplicateBelowGradeWall}
        deleteFn={wallFns.deleteWall}
        switchGradeFn={wallFns.switchWallGrade}
        switchDisabledReason={getSwitchDisabledReason(wall)}
        updateDefaultSizingFn={applyToDefaultSizingItems}
        defaultExpandAdvanced={opts?.defaultExpandAdvanced}
        focusFenestrationId={focusFenestrationId}
      />
    );

  const renderWallList = (walls: (AboveGradeWallType | BelowGradeWallType)[]) =>
    walls.map((wall) => renderWall(wall));

  const allWalls = useMemo(
    () => [
      ...(sizingAttributes?.aboveGradeWalls ?? []),
      ...(sizingAttributes?.belowGradeWalls ?? [])
    ],
    [sizingAttributes]
  );

  const selectedWall = useMemo(
    () => allWalls.find((wall) => wall.uniqueId === selectedWallId) ?? null,
    [allWalls, selectedWallId]
  );

  const selectedSideWalls = useMemo(() => {
    if (!selectedWall) return [];
    return [selectedWall];
  }, [selectedWall]);

  const ceilingsBelowRoof = sizingAttributes?.ceilingsBelowRoof || [];
  const ceilingsBelowAttic = sizingAttributes?.ceilingsBelowAttic || [];
  const floorsOverUnconditionedSpace =
    sizingAttributes?.floorsOverUnconditionedSpace || [];
  const floorsOverExposedSpace = sizingAttributes?.floorsOverExposedSpace || [];
  const slabFloors = sizingAttributes?.slabFloors || [];
  const basementFloors = sizingAttributes?.basementFloors || [];

  const hasCeilings =
    ceilingsBelowRoof.length > 0 || ceilingsBelowAttic.length > 0;

  const renderCeilingsSection = () => {
    if (!hasCeilings) return null;

    return (
      <div className='flex w-full flex-col gap-3'>
        <div className='text-xl font-bold text-gray-900'>Ceilings</div>
        {ceilingsBelowRoof.map((ceiling) => (
          <CeilingBelowRoofComponent
            key={ceiling.uniqueId}
            ceiling={ceiling}
            updateFn={ceilingFns.updateCeilingBelowRoof}
            duplicateFn={ceilingFns.duplicateCeilingBelowRoof}
            deleteFn={ceilingFns.deleteCeiling}
            updateDefaultSizingFn={applyToDefaultSizingItems}
          />
        ))}
        {ceilingsBelowAttic.map((ceiling) => (
          <CeilingBelowAtticComponent
            key={ceiling.uniqueId}
            ceiling={ceiling}
            updateFn={ceilingFns.updateCeilingBelowAttic}
            duplicateFn={ceilingFns.duplicateCeilingBelowAttic}
            deleteFn={ceilingFns.deleteCeiling}
            updateDefaultSizingFn={applyToDefaultSizingItems}
          />
        ))}
      </div>
    );
  };

  const hasFloors =
    floorsOverUnconditionedSpace.length > 0 ||
    floorsOverExposedSpace.length > 0 ||
    slabFloors.length > 0 ||
    basementFloors.length > 0;

  const renderFloorsSection = () => {
    if (!hasFloors) return null;

    return (
      <div className='flex w-full flex-col gap-3'>
        <div className='text-xl font-bold text-gray-900'>Floors</div>
        {floorsOverUnconditionedSpace.map((floor) => (
          <FloorOverUnconditionedSpaceComponent
            key={floor.uniqueId}
            floor={floor}
            updateFn={floorFns.updateFloorOverUnconditionedSpace}
            duplicateFn={floorFns.duplicateFloorOverUnconditionedSpace}
            deleteFn={floorFns.deleteFloor}
            updateDefaultSizingFn={applyToDefaultSizingItems}
          />
        ))}
        {floorsOverExposedSpace.map((floor) => (
          <FloorOverExposedSpaceComponent
            key={floor.uniqueId}
            floor={floor}
            updateFn={floorFns.updateFloorOverExposedSpace}
            duplicateFn={floorFns.duplicateFloorOverExposedSpace}
            deleteFn={floorFns.deleteFloor}
            updateDefaultSizingFn={applyToDefaultSizingItems}
          />
        ))}
        {slabFloors.map((floor) => (
          <SlabFloorComponent
            key={floor.uniqueId}
            floor={floor}
            updateFn={floorFns.updateSlabFloor}
            duplicateFn={floorFns.duplicateSlabFloor}
            deleteFn={floorFns.deleteFloor}
            updateDefaultSizingFn={applyToDefaultSizingItems}
          />
        ))}
        {basementFloors.map((floor) => (
          <BasementFloorComponent
            key={floor.uniqueId}
            floor={floor}
            updateFn={floorFns.updateBasementFloor}
            duplicateFn={floorFns.duplicateBasementFloor}
            deleteFn={floorFns.deleteFloor}
            updateDefaultSizingFn={applyToDefaultSizingItems}
          />
        ))}
      </div>
    );
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='text-xl font-bold text-gray-900'>Exterior Walls</div>

      <div className='flex w-full justify-center'>
        {space ? (
          <SpacePolygonWalls
            space={space}
            footprints={footprints}
            walls={allWalls}
            homeOrientation={homeOrientation}
            selectedWallId={selectedWallId}
            onSelectWall={setSelectedWallId}
          />
        ) : (
          <div className='flex h-32 w-48 items-center justify-center rounded border-2 border-gray-300 bg-gray-50 text-sm text-gray-500'>
            Space
          </div>
        )}
      </div>

      {selectedWall && (
        <div className='flex w-full flex-col gap-3'>
          {selectedSideWalls.map((wall) =>
            renderWall(wall, {
              defaultExpandAdvanced: wall.uniqueId === selectedWallId
            })
          )}
        </div>
      )}

      {wallsByEdge.unpositioned.length > 0 && (
        <div className='flex w-full flex-col gap-2'>
          <div className='text-sm font-medium text-gray-600'>
            Other Directions
          </div>
          {renderWallList(wallsByEdge.unpositioned)}
        </div>
      )}

      {renderCeilingsSection()}

      {renderFloorsSection()}
    </div>
  );
}
